import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import {
  ShipmentOrderLegRunForPlanEntity,
  ShipmentOrderLegRunListResponse,
} from 'src/entities/ShipmentOrderLegRun.entity';

export const SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY = 'shipment-order-leg-runs';

export const buildLegRunsRequestPath = (startOn: string, endOn: string, page: number, perPage: number) =>
  `/api/v5/shipment_order_leg_runs?start_on=${startOn}&end_on=${endOn}&page=${page}&per_page=${perPage}`;

/**
 * 未割当案件一覧の全ページを取り切る。page 1 で総ページ数を見て、残りは並列で取る。
 *
 * Why not useInfiniteQuery を呼び出し側の useEffect で 1 ページずつ進める（以前の形）:
 * 62 台規模では 5 ページ・2.7MB を**直列**に取っており、ページが届くたびに未割当リストと
 * 中央ボードが描き直されていた（log_web_issues#301 の計測）。delivery 側と同じ理由。
 *
 * 取得した案件を個別取得のキャッシュへ撒く副作用は従来どおり残す。落とすと
 * useQueryShipmentOrderLegRun が詳細を開くたびに 1 件ずつ取り直す。
 */
export const fetchAllLegRunPages = async (
  queryClient: QueryClient,
  startOn: string,
  endOn: string,
  perPage: number,
): Promise<ShipmentOrderLegRunListResponse[]> => {
  const getPage = (page: number): Promise<AxiosResponse<ShipmentOrderLegRunListResponse>> =>
    axios.get(buildLegRunsRequestPath(startOn, endOn, page, perPage));

  const first = await getPage(1);
  const totalPages = first.data.total_pages_count;
  const pages = totalPages <= 1
    ? [first.data]
    : [
      first.data,
      ...(await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) => index + 2).map((page) => getPage(page)),
      )).map((response) => response.data),
    ];

  pages.forEach((page) => page.data.forEach((entity) => {
    queryClient.setQueryData([SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY, { id: entity.id }], entity);
  }));

  return pages;
};

const fetchShipmentOrderLegRun = (id: number) => async () => {
  const response: AxiosResponse<ShipmentOrderLegRunForPlanEntity> = await axios.get(`/api/v5/shipment_order_leg_runs/${id}`);
  return response.data;
};

/** 全ページを 1 度に取るので「次のページ」は無い。呼び出し側の infinite 互換のために形だけ返す。 */
const NO_NEXT_PAGE = {
  hasNextPage: false,
  isFetchingNextPage: false,
  fetchNextPage: () => Promise.resolve(undefined),
} as const;

/**
 * 未割当案件一覧の取得口。/map・カード配車・V3Plans・V3Timeline が共有する。
 *
 * 中身は useQuery で全ページを取り切る（fetchAllLegRunPages）。**queryFn が InfiniteData
 * （`{ pages, pageParams }`）を返すので、QueryClient に入る形は useInfiniteQuery のときと同じ。**
 * 呼び出し側の `data.pages.flatMap(...)` も、キャッシュを直接触る既存処理もそのまま動く。
 * `hasNextPage` は常に false。
 */
export const useInfiniteQueryShipmentOrderLegRuns = (startOn: string, endOn: string, perPage: number) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    // queryFn の戻り値がそのまま QueryClient に入る。**ここで InfiniteData の形にしておく**
    // （フックの出口で包み直すと、キャッシュの中身は配列のままになる）。自動配車完了時の
    // markLegRunsUnassigned は `old.pages` が無いと黙って何もしないので、形が崩れると
    // 未割当化の即時反映が静かに効かなくなる
    queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY, startOn, endOn, perPage],
    queryFn: async () => {
      const pages = await fetchAllLegRunPages(queryClient, startOn, endOn, perPage);
      return { pages, pageParams: pages.map((_, index) => index + 1) };
    },
    enabled: Boolean(startOn) && Boolean(endOn),
    staleTime: Infinity,
  });

  return useMemo(() => ({ ...query, ...NO_NEXT_PAGE }), [query]);
};

/** 上のフックの戻り値。呼び出し側の props 型に使う。 */
export type ShipmentOrderLegRunsQueryResult = ReturnType<typeof useInfiniteQueryShipmentOrderLegRuns>;

export const useQueryShipmentOrderLegRun = (id: number) => useQuery({
  queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY, { id }],
  queryFn: fetchShipmentOrderLegRun(id),
  enabled: Boolean(id),
  staleTime: Infinity,
});
